import React, { useState } from "react";
import "./ticketCard.scss";
import { IStub, ITicket } from "redux/reducers/models/ticket";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeftArrowRight,
  faArrowTurnRight,
  faChevronDown,
  faChevronUp,
  faCircleInfo,
  faTicket,
} from "@fortawesome/pro-regular-svg-icons";
import { Badge } from "components/badge/Badge";
import { ButtonNew as Button } from "components/buttonNew";
import { useTranslation } from "react-i18next";
import Markdown from "components/markdown/Markdown";
import moment from "moment";
import { capitalize } from "helpers/Helpers";
import { faCircle } from "@fortawesome/pro-solid-svg-icons";
import { ITicketStubExtended } from "../PortalTickets";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { TicketStubType } from "pages/secure/facility/customer/context/contextTypes";

interface ISharedTicket {
  id: number;
  shared_by_customer_id: number;
  shared_with_customer_id: number;
  shared_with_customer: { id: number; first_name: string; last_name: string };
  ticket_id: number;
}

interface IProps {
  ticket: ITicket & { stubs: Array<IStub & { is_shared?: boolean }>; shared_with?: Array<ISharedTicket> };
  activeStubs: number;
  totalStubsCount: number;
  onStubClick?: (stub: ITicketStubExtended) => void;
  onTransferClick: (e: React.MouseEvent, ticket: ITicket) => void;
  onShareClick?: (e: React.MouseEvent, ticket: ITicket) => void;
  displaySharedWith?: boolean;
}

export default function TicketCard(props: IProps) {
  const { ticket, activeStubs, totalStubsCount, onStubClick, onTransferClick, onShareClick, displaySharedWith } = props;
  const { t } = useTranslation();
  const [open, setOpen] = useState<boolean>(false);
  const canShareTransfer = !ticket?.stubs?.every(stub => stub?.is_shared);

  function getBadgeType(stubStatus: string) {
    switch (stubStatus) {
      case "void":
        return "error";
      case "redeemed":
        return "gray";
      case "valid":
        return "success";
      case "transfer_pending":
        return "warning";
      case "expired":
        return "gray";
      default:
        return "gray";
    }
  }

  function orderStubs(stubs: Array<TicketStubType & { is_shared?: boolean }>) {
    const statusPriority: Record<IStub["status"], number> = {
      valid: 1,
      expired: 2,
      redeemed: 3,
      void: 4,
      canceled: 5,
    };
    const sortedTickets = [...stubs].sort((a, b) => {
      const statusDiff = statusPriority[a.status] - statusPriority[b.status];
      if (statusDiff !== 0) {
        return statusDiff;
      }

      const dateA =
        a.status === "redeemed" ? new Date(a.redeemed_at ?? 0)?.getTime() : new Date(a.created_at)?.getTime();
      const dateB =
        b.status === "redeemed" ? new Date(b.redeemed_at ?? 0)?.getTime() : new Date(b.created_at)?.getTime();

      return dateB - dateA;
    });
    return sortedTickets;
  }

  return (
    <div className="ui-ticket-card">
      <div className="ui-ticket-card-header" onClick={() => setOpen(prev => !prev)}>
        <div className="ui-ticket-card-left">
          <div className="ui-ticket-card-flex-row">
            {!ticket.logo_image ? (
              <FontAwesomeIcon className="customer-ticket-logo" icon={faTicket} />
            ) : (
              <img className="customer-ticket-logo" src={ticket.logo_image} />
            )}
            <div className="ui-ticket-card-flex-column">
              <p className="ui-ticket-card-title">
                {ticket?.title}{" "}
                <Badge size="small" type="gray">
                  {activeStubs} / {totalStubsCount}
                </Badge>
              </p>
              <p className="ui-ticket-card-subtitle">{ticket?.subtitle}</p>
            </div>
          </div>
          <div className="ui-ticket-card-divider"></div>
          <p className="ui-ticket-card-subtitle">{t("Ticket includes: ")}</p>
          <div className="ui-ticket-card-bottom">
            <div className="ui-ticket-card-flex-row flex-wrap">
              {ticket?.variant_redemption?.map((variant, index) => {
                return (
                  <div key={index} className="ui-ticket-card-tag">
                    {variant?.redemption_details?.quantity}{" "}
                    {variant?.title !== variant?.product?.title
                      ? `${
                          variant?.product?.preferred_title
                            ? variant?.product?.preferred_title
                            : variant?.product?.title
                        } - ${variant?.title}`
                      : variant?.title}
                  </div>
                );
              })}
            </div>
            <div className="ui-ticket-card-flex-row">
              {onTransferClick && canShareTransfer && !ticket?.transfers_disabled ? (
                <Button
                  size="small"
                  type="text"
                  onClick={e => onTransferClick(e, ticket)}
                  disabled={activeStubs <= 0 || ticket?.transfers_disabled}
                >
                  {t("Transfer")} <FontAwesomeIcon className="ml-2" icon={faArrowLeftArrowRight} />
                </Button>
              ) : null}
              {displaySharedWith && ticket?.shared_with?.length > 0 ? (
                <div>
                  <p className="ui-ticket-card-shared-with">
                    {t("Shared with: ")}
                    <Badge size="small" type="gray">
                      {ticket?.shared_with[0]?.shared_with_customer?.first_name?.charAt(0)}
                      {ticket?.shared_with[0]?.shared_with_customer?.last_name?.charAt(0)}
                    </Badge>{" "}
                    {ticket?.shared_with?.length > 1 ? `+ ${ticket?.shared_with?.length - 1}` : null}
                  </p>
                </div>
              ) : null}
              {onShareClick && canShareTransfer ? (
                <div className="flex items-center">
                  <Button
                    size="small"
                    type="text"
                    onClick={e => onShareClick(e, ticket)}
                    disabled={ticket?.share_limit <= 0}
                  >
                    {t("Share")} <FontAwesomeIcon className="ml-2" icon={faArrowTurnRight} />
                  </Button>
                  {ticket?.share_limit <= 0 ? (
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        ticket?.share_limit <= 0 ? (
                          <Tooltip id={`share_ticket_tool_tip`}>
                            {t("This ticket is not eligible to be shared with other users")}
                          </Tooltip>
                        ) : null
                      }
                    >
                      <FontAwesomeIcon icon={faCircleInfo} className="ticket-card-tooltip" />
                    </OverlayTrigger>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        </div>
        <div className="ui-ticket-card-right">
          <Button size="medium" type="secondary" onClick={() => {}}>
            <FontAwesomeIcon icon={open ? faChevronUp : faChevronDown} />
          </Button>
        </div>
      </div>
      {open ? (
        <div>
          <p className="ui-ticket-card-stub-title">
            <b>{ticket?.included}</b>
          </p>
          <Markdown markdownText={ticket?.description ?? ""} />
          <br />
          {orderStubs(ticket?.stubs)
            ?.filter(stub => (displaySharedWith ? !stub?.is_shared : true))
            ?.map((stub, stubIndex) => {
              const dateExpired = stub?.expiry_date ? moment()?.isSameOrAfter(moment(stub?.expiry_date)) : false;
              const badgeType = getBadgeType(stub?.status);
              return (
                <div
                  key={stubIndex}
                  className="ui-ticket-card-stub-card"
                  onClick={
                    stub?.status === "valid"
                      ? () => onStubClick({ ...stub, badgeStatus: badgeType, ticket: { ...ticket, selected: false } })
                      : undefined
                  }
                >
                  <div>
                    <p className="ui-ticket-card-stub-title">{ticket?.title}</p>
                    <p className="ui-ticket-card-stub-subtitle">
                      {stub?.code}{" "}
                      {stub?.redeemed_at
                        ? ` • ${t("Redeemed on")} `
                        : dateExpired
                        ? ` • ${t("Expired on")} `
                        : stub?.expiry_date
                        ? ` • ${t("Expires on")} `
                        : ""}
                      {stub?.redeemed_at
                        ? moment(stub?.redeemed_at).format("MMMM Do, YYYY")
                        : stub?.expiry_date
                        ? moment(stub?.expiry_date).format("MMMM Do, YYYY")
                        : ""}
                    </p>
                  </div>
                  <div className="ui-ticket-card-flex-row">
                    {stub?.is_shared ? (
                      <Badge size="medium" type="lightblue">
                        {t("Shared with you")}
                      </Badge>
                    ) : null}
                    <Badge
                      size="medium"
                      type={badgeType}
                      iconLeft={
                        stub.status === "valid" || stub.status === "void" ? (
                          <FontAwesomeIcon className="customer-ticket-badge-icon" icon={faCircle} />
                        ) : undefined
                      }
                    >
                      {capitalize(stub?.status)}
                    </Badge>
                    {stub?.status === "valid" ? (
                      <Button size="small" type="secondary" onClick={() => {}}>
                        {t("Redeem")}
                      </Button>
                    ) : null}
                  </div>
                </div>
              );
            })}
        </div>
      ) : null}
    </div>
  );
}
